<script setup>

import PoweredByRoori from "../PoweredByRoori.vue";
import {computed, inject, ref} from "vue";
import {percentToHex as pth} from "../../../../../helpers.js";
import {useWindowSize} from "@vueuse/core";

const route = inject('route');
const { width } = useWindowSize();

const props = defineProps({
    theme: Object,
    forms: Object,
    width: Number,
    isLast: Boolean,
})

const emit = defineEmits(['toggle-dark', 'reload-events'])

const reloading = ref(false)
const reloadCooldown = ref(false)
const reloadEvents = () => {
    if (reloadCooldown.value) return
    reloading.value = true
    reloadCooldown.value = true
    emit('reload-events')
    setTimeout(() => { reloading.value = false }, 1000)
    setTimeout(() => { reloadCooldown.value = false }, 5000)
}

const fontSizes = [
    { label: 'A', value: '14px' },
    { label: 'A', value: '16px' },
    { label: 'A', value: '18px' },
]
const activeFontSize = ref('14px')
const highContrast = ref(false)

const paneFilter = computed(() => highContrast.value ? 'contrast(1.5) saturate(0.7)' : undefined)

const percentToHex = (value) => pth(value)
const previewStyle = computed(() => ({
    backgroundImage: props.theme.background_image_url ? `url(${props.theme.background_image_url})` : '',
    backgroundColor: `#${props.theme.background_color}`,
}));

const bodyClass = computed(() => {
    return {
        'md:rounded-xl': props.theme.roundness === 1,
        'md:rounded-2xl': props.theme.roundness === 2,
        'md:rounded-3xl': props.theme.roundness === 3,
        'backdrop-filter backdrop-blur-lg': props.theme.panel_backdrop_blur,
        'md:border': props.theme.panel_transparency > 50,
        'border-neutral-700': props.theme.darkMode
    };
})

const bodyStyle = computed(() => {
    let assertedWidth = props.width;
    if (assertedWidth > width.value) {
        assertedWidth = width.value;
    }

    return {
        backgroundColor: `#${props.theme.panel_color}${percentToHex(props.theme.panel_transparency)}`,
        width: `${assertedWidth}px`
    }
})
</script>

<template>
    <div class="w-full h-full overflow-x-hidden bg-no-repeat bg-cover md:flex flex-col items-center justify-center pt-8 pb-0 height-animate min-h-dvh" :style="previewStyle">
        <div id="pane" class="color-transition transition-all md:border flex max-lg:flex-col overflow-hidden max-w-[1600px] md:min-h-[600px] scale-in-center" :class="bodyClass" :style="{ ...bodyStyle, fontSize: activeFontSize, filter: paneFilter }">
            <div class="p-6 lg:p-8 md:bg-neutral-200/20 max-lg:border-b dark:border-neutral-700 dark:bg-neutral-800/20 lg:border-r min-h-full max-lg:gap-4 flex flex-col lg:!w-1/4 lg:min-w-[290px]">
                <slot name="sidebar"></slot>
                <div class="mt-auto pt-4 flex flex-wrap justify-center items-center gap-1">
                    <!-- Velikost textu -->
                    <button
                        v-for="(size, i) in fontSizes"
                        :key="size.value"
                        @click="activeFontSize = size.value"
                        :class="[
                            activeFontSize === size.value
                                ? 'bg-neutral-200 dark:bg-neutral-700 font-semibold'
                                : 'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                            i === 0 ? 'text-xs' : i === 1 ? 'text-sm' : 'text-base'
                        ]"
                        class="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-600 dark:text-neutral-300 transition-colors"
                        :title="i === 0 ? $t('widget.text_size_normal') : i === 1 ? $t('widget.text_size_large') : $t('widget.text_size_largest')"
                    >{{ size.label }}</button>

                    <div class="w-px h-5 bg-neutral-300 dark:bg-neutral-600 mx-1"></div>

                    <!-- Reload eventů -->
                    <button
                        @click="reloadEvents"
                        :disabled="reloadCooldown"
                        :class="reloadCooldown ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                        class="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-600 dark:text-neutral-300 transition-colors"
                        :title="$t('widget.reload_slots')"
                    ><span class="pi pi-refresh text-xs" :class="{ 'animate-spin': reloading }"></span></button>

                    <div class="w-px h-5 bg-neutral-300 dark:bg-neutral-600 mx-1"></div>

                    <!-- Vyšší kontrast -->
                    <button
                        @click="highContrast = !highContrast"
                        :class="highContrast ? 'bg-neutral-200 dark:bg-neutral-700 font-semibold' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                        class="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-600 dark:text-neutral-300 transition-colors"
                        :title="$t('widget.high_contrast')"
                    ><span class="pi pi-circle-fill text-xs"></span></button>

                    <!-- Dark mode -->
                    <button
                        v-if="!theme.darkModeLock"
                        @click="emit('toggle-dark')"
                        :class="theme.darkMode ? 'bg-neutral-200 dark:bg-neutral-700 font-semibold' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                        class="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-600 dark:text-neutral-300 transition-colors"
                        :title="theme.darkMode ? $t('forms.theme_mode_light') : $t('forms.theme_mode_dark')"
                    ><span :class="theme.darkMode ? 'pi pi-sun' : 'pi pi-moon'" class="text-xs"></span></button>
                </div>
            </div>
            <div class="lg:w-3/4 flex flex-col min-h-full max-md:pb-16">
                <slot></slot>
                <div class="grow"></div>
                <div v-if="!isLast" class="bg-neutral-50/80 dark:bg-neutral-900 dark:border-neutral-700 w-full max-md:hidden border-t p-2 md:p-4">
                    <slot name="bottombar"></slot>
                </div>
            </div>
        </div>
        <div v-if="!isLast" class="bg-neutral-50 backdrop-blur dark:bg-neutral-900 dark:border-neutral-700 w-full md:hidden fixed bottom-0 border-t p-2 md:p-4">
            <slot name="bottombar"></slot>
        </div>
        <a href="https://roori.app?utm_source=roori&utm_medium=reservation_form&utm_campaign=powered_by_roori" v-if="forms.show_banner" class="text-xs scale-in-center max-md:hidden mt-2">
            <span class="opacity-45">{{ $t('widget.powered_by', { year: new Date().getFullYear() }) }}</span>
        </a>

        <div class="container mx-auto flex flex-col items-center justify-center gap-4 md:py-6">
            <powered-by-roori v-if="forms.show_banner"></powered-by-roori>
        </div>
    </div>
</template>

<style scoped lang="scss">
.scale-in-center {
    animation: scale-in-center 0.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: .4s;

    transform: scale(0.95);
    opacity: 0;
}
/* ----------------------------------------------
 * Generated by Animista on 2025-8-21 20:31:8
 * Licensed under FreeBSD License.
 * See http://animista.net/license for more info.
 * w: http://animista.net, t: @cssanimista
 * ---------------------------------------------- */

/**
 * ----------------------------------------
 * animation scale-in-center
 * ----------------------------------------
 */
@keyframes scale-in-center {
    0% {
        transform: scale(0.95);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

</style>
